import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import "./language-switcher.css";

const languages = [
  { code: "en", key: "EN", name: "English", flag: "/assets/images/usa-flag.png" },
  { code: "de", key: "DE", name: "Deutsch", flag: "/assets/images/germany-flag.png" }
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Initialize only once
  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLanguage");

    if (savedLang) {
      i18n.changeLanguage(savedLang).then(() => setLoading(false));
    } else {
      fetch("https://ipapi.co/json/")
        .then((res) => res.json())
        .then((data) => {
          const lang = data.country_code === "DE" ? "de" : "en";
          i18n.changeLanguage(lang);
          localStorage.setItem("selectedLanguage", lang);

          if (lang === "de" && !location.pathname.startsWith("/de")) {
            navigate("/de" + location.pathname, { replace: true });
          }
        })
        .catch(() => {
          i18n.changeLanguage("en");
          localStorage.setItem("selectedLanguage", "en");
        })
        .finally(() => setLoading(false));
    }
  }, [i18n, location.pathname, navigate]);

  // ✅ Language toggle logic with URL sync
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("selectedLanguage", lng);
    setOpen(false);

    const currentPath = location.pathname;

    if (lng === "de" && !currentPath.startsWith("/de")) {
      // Add /de prefix
      navigate("/de" + currentPath, { replace: true });
    } else if (lng === "en" && currentPath.startsWith("/de")) {
      // Remove /de prefix
      navigate(currentPath.replace("/de", "") || "/", { replace: true });
    }
  };

  // ✅ Only show the other language in dropdown
  const currentLang =
    languages.find((lang) => lang.code === i18n.language) || languages[0];
  const otherLanguages = languages.filter(
    (lang) => lang.code !== currentLang.code
  );

  if (loading) return null;

  return (
    <div className="language-switcher">
      <button className="lang-btn" type="button" onClick={() => setOpen((prev) => !prev)}>
        <img
          src={currentLang.flag}
          className="language-flag"
          alt={currentLang.code}
        />
        <span>{currentLang.key}</span>
        <i className="fa-solid fa-chevron-down"></i>
      </button>

      {open && (
        <ul className="language-dropdown">
          {otherLanguages.map((lang) => (
            <li key={lang.code} onClick={() => changeLanguage(lang.code)}>
              <img
                src={lang.flag}
                className="language-flag"
                alt={lang.code}
              />
              <span>{lang.key}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
